// ProfileEdit Component
// Main component for editing player profiles

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaUser, FaRunning, FaBriefcase, FaImage, FaCog } from 'react-icons/fa';
import styles from './ProfileEdit.module.css';
import { usePlayerProfile } from '../../hooks/playerProfile/usePlayerProfile';
import { useNotification } from '../../context/NotificationContext';
import { useUser } from '../../context/UserContext';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';

// Import form sections
import BasicInfoForm from './forms/BasicInfoForm';
import AthleticForm from './forms/AthleticForm';
import SkillsForm from './forms/SkillsForm';
import ExperienceForm from './forms/ExperienceForm';
import MediaForm from './forms/MediaForm';
import AvailabilityForm from './forms/AvailabilityForm';
import PrivacySettings from './forms/PrivacySettings';

const ProfileEdit = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useNotification();
  const { user: currentUser, userProfile: contextUserProfile } = useUser();

  // State
  const [activeSection, setActiveSection] = useState('basic');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sectionSaving, setSectionSaving] = useState({});
  const [formData, setFormData] = useState(null);

  // Hooks
  const {
    profile,
    loading,
    error,
    isOwner,
    profileNotFound,
    updateProfile,
    updatePrivacyBulk,
    refreshProfile
  } = usePlayerProfile(userId);

  // Check authorization - but allow if viewing own profile even if profile doesn't exist yet
  useEffect(() => {
    if (!loading) {
      const isOwnProfile = 
        isOwner || 
        (contextUserProfile && contextUserProfile._id === userId) ||
        (currentUser && currentUser.uid === userId);
        
      if (!isOwnProfile) {
        showError('You can only edit your own profile');
        navigate(`/player-profile/${userId}`);
      }
    }
  }, [loading, isOwner, navigate, showError, userId, currentUser, contextUserProfile]);

  // Initialize form data from profile OR with empty data for new profiles
  useEffect(() => {
    // Only initialize once, OR re-initialize if profile changes significantly
    // (e.g., after first save when profile didn't exist)
    const shouldInitialize = !formDataInitialized.current || 
                            (profileNotFound === false && formData === null);
    
    if (shouldInitialize && !loading) {
      formDataInitialized.current = true;
      
      
      // Extract profile picture URL from backend
      // Backend saves to: media.profileImage.cloudinary.url
      const profilePictureUrl = profile?.media?.profileImage?.cloudinary?.url || 
                                profile?.media?.profileImage?.cloudinary?.transformedUrls?.medium ||
                                profile?.profilePicture || '';
      
      // Extract cover photo URL from backend
      // Backend can save as: string URL, or { cloudinary: {...} }, or { url: '...' }
      let coverPhotoUrl = '';
      if (typeof profile?.coverPhoto === 'string') {
        coverPhotoUrl = profile.coverPhoto;
      } else if (profile?.coverPhoto?.cloudinary?.url) {
        coverPhotoUrl = profile.coverPhoto.cloudinary.url;
      } else if (profile?.coverPhoto?.url) {
        coverPhotoUrl = profile.coverPhoto.url;
      } else if (profile?.coverPhoto?.cloudinary?.transformedUrls?.desktop) {
        coverPhotoUrl = profile.coverPhoto.cloudinary.transformedUrls.desktop;
      }
      
      // Use existing profile data or initialize with empty values
      setFormData({
        // Basic Info
        profilePicture: profilePictureUrl,
        firstName: profile?.firstName || profile?.userId?.firstName || '',
        lastName: profile?.lastName || profile?.userId?.lastName || '',
        gender: profile?.gender || '',
        headline: profile?.headline || '',
        bio: profile?.bio || '',
        coverPhoto: coverPhotoUrl,
        location: profile?.location || {
          city: '',
          state: '',
          country: ''
        },
        dateOfBirth: profile?.dateOfBirth ? new Date(profile.dateOfBirth).toISOString().split('T')[0] : '',
        contactPreferences: {
          email: {
            visible: profile?.contactPreferences?.email?.visible || false,
            value: profile?.contactPreferences?.email?.value || currentUser?.email || contextUserProfile?.email || ''
          },
          phone: {
            visible: profile?.contactPreferences?.phone?.visible || false,
            value: profile?.contactPreferences?.phone?.value || ''
          },
          preferredMethod: profile?.contactPreferences?.preferredMethod || 'inApp'
        },
        
        // Athletic Profile
        athleticProfile: (() => {
          const athleticProfile = profile?.athleticProfile || {
            primarySport: '',
            secondarySports: [],
            positions: [],
            physicalAttributes: {},
            performanceMetrics: {}
          };
          
          // Migrate old height/weight format to new format
          if (athleticProfile.physicalAttributes) {
            const pa = athleticProfile.physicalAttributes;
            
            // Convert height if in old format
            if (pa.height !== undefined && typeof pa.height !== 'object') {
              athleticProfile.physicalAttributes.height = {
                value: pa.height,
                unit: pa.heightUnit || 'cm'
              };
            }
            
            // Convert weight if in old format
            if (pa.weight !== undefined && typeof pa.weight !== 'object') {
              athleticProfile.physicalAttributes.weight = {
                value: pa.weight,
                unit: pa.weightUnit || 'kg'
              };
            }
          }
          
          return athleticProfile;
        })(),
        
        // Skills
        skills: profile?.skills || {
          technical: [],
          tactical: [],
          physical: [],
          mental: []
        },
        
        // Experience
        experience: profile?.experience || [],
        
        // Media
        media: profile?.media || {
          highlightVideos: [],
          photos: [],
          documents: []
        },
        
        // Availability
        availability: profile?.availability ? {
          ...profile.availability,
          // Format startDate for HTML date input (YYYY-MM-DD)
          startDate: profile.availability.startDate 
            ? new Date(profile.availability.startDate).toISOString().split('T')[0]
            : null
        } : {
          status: 'not_looking',
          startDate: null,
          preferredLocations: [],
          willingToRelocate: false,
          travelRadius: null,
          preferredLevels: [],
          preferredCommitment: null,
          minimumCompensation: null,
          notes: ''
        },
        
        // Privacy Settings
        privacySettings: profile?.privacySettings || {
          basicInfo: 'public',
          athleticProfile: 'public',
          awards: 'public',
          clubHistory: 'clubs',
          events: 'clubs',
          skills: 'public',
          media: 'public',
          availability: 'clubs'
        }
      });
      
    }
  }, [loading, profile]); // Initialize once with profile data

  // Auto-save timer ref
  const autoSaveTimerRef = useRef(null);
  
  // Track if form data has been initialized
  const formDataInitialized = useRef(false);
  
  // Reset initialization flag when userId changes (navigating to different profile)
  useEffect(() => {
    formDataInitialized.current = false;
    setFormData(null); // Reset form data
  }, [userId]);
  
  // Sync formData.experience when profile.experience changes (after save)
  useEffect(() => {
    if (profile?.experience && formData && !hasUnsavedChanges) {
      // Only sync if we're not currently editing (no unsaved changes)
      // This prevents overwriting user's local changes
      const profileExperienceStr = JSON.stringify(profile.experience);
      const formDataExperienceStr = JSON.stringify(formData.experience);
      
      if (profileExperienceStr !== formDataExperienceStr) {
        setFormData(prev => ({
          ...prev,
          experience: profile.experience
        }));
      }
    }
  }, [profile?.experience, hasUnsavedChanges]);

  // Sync formData.availability when profile.availability changes (after save)
  useEffect(() => {
    if (profile?.availability && formData && !hasUnsavedChanges) {
      // Only sync if we're not currently editing (no unsaved changes)
      // This prevents overwriting user's local changes
      const profileAvailabilityStr = JSON.stringify(profile.availability);
      const formDataAvailabilityStr = JSON.stringify(formData.availability);
      
      if (profileAvailabilityStr !== formDataAvailabilityStr) {
        // Format dates for HTML inputs (YYYY-MM-DD only)
        const formatDateForInput = (dateString) => {
          if (!dateString) return null;
          try {
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
          } catch (e) {
            return null;
          }
        };
        
        setFormData(prev => ({
          ...prev,
          availability: {
            ...profile.availability,
            startDate: formatDateForInput(profile.availability.startDate)
          }
        }));
      }
    }
  }, [profile?.availability, hasUnsavedChanges]);

  // Sync formData.privacySettings when profile.privacySettings changes (after save)
  useEffect(() => {
    if (profile?.privacySettings && formData && !hasUnsavedChanges) {
      // Only sync if we're not currently editing (no unsaved changes)
      // This prevents overwriting user's local changes
      const profilePrivacyStr = JSON.stringify(profile.privacySettings);
      const formDataPrivacyStr = JSON.stringify(formData.privacySettings);
      
      if (profilePrivacyStr !== formDataPrivacyStr) {
        setFormData(prev => ({
          ...prev,
          privacySettings: profile.privacySettings
        }));
      }
    }
  }, [profile?.privacySettings, hasUnsavedChanges]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Handle form changes with debounced auto-save
  const handleFormChange = (section, data, options = {}) => {
    // Check if this is a save request
    if (options.shouldSave) {
      handleSectionSave(section, data);
      return;
    }
    
    // For basicInfo, we replace the entire formData since BasicInfoForm works with top-level fields
    if (section === 'basicInfo') {
      setFormData(data);
    } else {
      // For other sections, nest the data under the section key
      setFormData(prev => ({
        ...prev,
        [section]: data
      }));
    }
    setHasUnsavedChanges(true);
    
    // Debounced auto-save (save after 3 seconds of no changes)
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Capture the current section at the time of the change
    const sectionToSave = section;
    
    autoSaveTimerRef.current = setTimeout(async () => {
      // Use the captured section, not the current activeSection
      // This prevents saving the wrong section if user switches tabs
      
      // Use setFormData callback to get the latest state
      setFormData(currentFormData => {
        // Determine what data to save based on the section that changed
        let dataToSave;
        if (sectionToSave === 'basicInfo') {
          dataToSave = currentFormData;
        } else if (sectionToSave === 'privacy') {
          dataToSave = currentFormData.privacySettings;
        } else {
          dataToSave = currentFormData[sectionToSave];
        }
        
        // Save the section
        if (dataToSave) {
          handleSectionSave(sectionToSave, dataToSave);
        }
        
        return currentFormData; // Don't actually update the state
      });
    }, 3000);
  };

  // Handle saving individual sections
  const handleSectionSave = async (section, data) => {
    try {
      setSectionSaving(prev => ({ ...prev, [section]: true }));
      
      let dataToSave = {};
      
      // Prepare data based on section
      if (section === 'basicInfo') {
        // Extract only basic info fields
        dataToSave = {
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          gender: data.gender || '',
          headline: data.headline || '',
          bio: data.bio || '',
          location: data.location || {},
          dateOfBirth: data.dateOfBirth || null,
          // Always send complete contactPreferences structure to preserve visibility settings
          contactPreferences: {
            email: {
              visible: data.contactPreferences?.email?.visible || false,
              value: data.contactPreferences?.email?.value || ''
            },
            phone: {
              visible: data.contactPreferences?.phone?.visible || false,
              value: data.contactPreferences?.phone?.value || ''
            },
            preferredMethod: data.contactPreferences?.preferredMethod || 'inApp'
          }
        };
        
        // Only include images if they're URLs (not base64)
        if (data.profilePicture && !data.profilePicture.startsWith('data:')) {
          dataToSave.profilePicture = data.profilePicture;
        }
        
        if (data.coverPhoto && !data.coverPhoto.startsWith('data:')) {
          dataToSave.coverPhoto = data.coverPhoto;
        }
      } else if (section === 'athleticProfile') {
        dataToSave = { athleticProfile: data };
      } else if (section === 'skills') {
        dataToSave = { skills: data };
      } else if (section === 'experience') {
        dataToSave = { experience: data };
      } else if (section === 'media') {
        dataToSave = { media: data };
      } else if (section === 'availability') {
        dataToSave = { availability: data };
      } else if (section === 'privacy' || section === 'privacySettings') {
        // Handle privacy settings separately
        await updatePrivacyBulk(data);
        showSuccess(`Privacy settings saved successfully!`);
        setSectionSaving(prev => ({ ...prev, [section]: false }));
        setHasUnsavedChanges(false);
        return;
      }
      
      // Save the section data
      const response = await updateProfile(dataToSave);
      
      // Don't update formData - keep user's input as-is
      // The backend filters empty values, so we want to preserve what the user typed
      
      setHasUnsavedChanges(false);
      
      // Show subtle success indicator (optional - comment out for completely silent save)
      // showInfo(`${getSectionName(section)} saved`);
    } catch (err) {
      console.error(`Error saving ${section}:`, err);
      showError(`Failed to save ${getSectionName(section)}. Please try again.`);
    } finally {
      setSectionSaving(prev => ({ ...prev, [section]: false }));
    }
  };

  // Get user-friendly section name
  const getSectionName = (section) => {
    const names = {
      basicInfo: 'Basic Information',
      athleticProfile: 'Athletic Profile',
      skills: 'Skills',
      experience: 'Experience',
      media: 'Media',
      availability: 'Availability',
      privacy: 'Privacy Settings'
    };
    return names[section] || section;
  };

  // Handle navigation with auto-save
  const handleNavigation = async (section) => {
    // Auto-save current section if there are changes
    if (hasUnsavedChanges && activeSection !== section) {
      await autoSaveCurrentSection();
    }
    setActiveSection(section);
  };

  // Auto-save the current section
  const autoSaveCurrentSection = useCallback(async () => {
    const sectionMap = {
      basic: 'basicInfo',
      athletic: 'athleticProfile',
      skills: 'skills',
      experience: 'experience',
      media: 'media',
      availability: 'availability',
      privacy: 'privacy'
    };
    
    const dataSection = sectionMap[activeSection];
    if (!dataSection || !formData) return;
    
    let dataToSave;
    if (activeSection === 'basic') {
      // For basic info, extract only the relevant fields
      dataToSave = {
        profilePicture: formData.profilePicture || '',
        headline: formData.headline || '',
        bio: formData.bio || '',
        location: formData.location || {},
        dateOfBirth: formData.dateOfBirth || null,
        // Always send complete contactPreferences structure to preserve visibility settings
        contactPreferences: {
          email: {
            visible: formData.contactPreferences?.email?.visible || false,
            value: formData.contactPreferences?.email?.value || ''
          },
          phone: {
            visible: formData.contactPreferences?.phone?.visible || false,
            value: formData.contactPreferences?.phone?.value || ''
          },
          preferredMethod: formData.contactPreferences?.preferredMethod || 'inApp'
        }
      };
      
      // Only include coverPhoto if it's a URL (not base64)
      if (formData.coverPhoto && !formData.coverPhoto.startsWith('data:')) {
        dataToSave.coverPhoto = formData.coverPhoto;
      }
    } else if (activeSection === 'privacy') {
      dataToSave = formData.privacySettings;
    } else {
      dataToSave = formData[dataSection];
    }
    
    if (dataToSave) {
      await handleSectionSave(dataSection, dataToSave);
    }
  }, [activeSection, formData]);

  // Handle save
  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Extract privacy settings
      const { privacySettings, ...profileData } = formData;
      
      // Update profile data
      await updateProfile(profileData);
      
      // Update privacy settings
      await updatePrivacyBulk(privacySettings);
      
      // Refresh profile to get latest data
      await refreshProfile();
      
      setHasUnsavedChanges(false);
      showSuccess('Profile saved successfully!');
    } catch (err) {
      console.error('Error saving profile:', err);
      showError('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (hasUnsavedChanges) {
      setShowExitConfirmation(true);
    } else {
      navigate(`/player-profile/${userId}`);
    }
  };

  // Handle exit confirmation
  const handleExitConfirm = () => {
    setShowExitConfirmation(false);
    navigate(`/player-profile/${userId}`);
  };

  // Render loading state
  if (loading || !formData) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="large" />
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  // Render error state (but not for profileNotFound - that's normal for new profiles)
  if (error && !profileNotFound) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>Error Loading Profile</h2>
          <p>{error}</p>
          <button 
            className={styles.backButton}
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  // Navigation items
  const navigationItems = [
    { id: 'basic', label: 'Basic Info', icon: <FaUser /> },
    { id: 'athletic', label: 'Athletic Profile', icon: <FaRunning /> },
    { id: 'skills', label: 'Skills', icon: <FaBriefcase /> },
    { id: 'experience', label: 'Experience', icon: <FaBriefcase /> },
    { id: 'media', label: 'Media', icon: <FaImage /> },
    { id: 'availability', label: 'Availability', icon: <FaCog /> },
    { id: 'privacy', label: 'Privacy Settings', icon: <FaCog /> }
  ];

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <button 
          className={styles.backButton}
          onClick={handleBack}
        >
          <FaArrowLeft /> Back to Profile
        </button>
        
        <h1 className={styles.title}>
          {profileNotFound ? 'Create Your Profile' : 'Edit Profile'}
        </h1>
        
        <div className={styles.autoSaveStatus}>
          {Object.values(sectionSaving).some(saving => saving) ? (
            <span className={styles.savingStatus}>
              <LoadingSpinner size="small" color="currentColor" />
              Saving...
            </span>
          ) : hasUnsavedChanges ? (
            <span className={styles.unsavedStatus}>
              • Unsaved changes
            </span>
          ) : (
            <span className={styles.savedStatus}>
              ✓ All changes saved
            </span>
          )}
        </div>
      </div>

      <div className={styles.content}>
        {/* Navigation Sidebar */}
        <nav className={styles.sidebar}>
          {navigationItems.map(item => (
            <button
              key={item.id}
              className={`${styles.navItem} ${activeSection === item.id ? styles.active : ''}`}
              onClick={() => handleNavigation(item.id)}
              disabled={Object.values(sectionSaving).some(saving => saving)}
            >
              {item.icon}
              <span>{item.label}</span>
              {sectionSaving[item.id] && (
                <span className={styles.savingIndicator}>
                  <LoadingSpinner size="small" color="currentColor" />
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Form Content */}
        <div className={styles.formContent}>
          {activeSection === 'basic' && (
            <BasicInfoForm
              data={formData}
              userId={userId}
              onChange={(data, options) => handleFormChange('basicInfo', data, options)}
              saving={sectionSaving.basicInfo}
            />
          )}
          
          {activeSection === 'athletic' && (
            <AthleticForm
              data={formData.athleticProfile}
              onChange={(data, options) => handleFormChange('athleticProfile', data, options)}
              saving={sectionSaving.athleticProfile}
            />
          )}
          
          {activeSection === 'skills' && (
            <SkillsForm
              data={formData.skills}
              onChange={(data) => handleFormChange('skills', data)}
            />
          )}
          
          {activeSection === 'experience' && (
            <ExperienceForm
              data={formData.experience}
              onChange={(data) => handleFormChange('experience', data)}
            />
          )}
          
          {activeSection === 'media' && (
            <MediaForm
              media={formData.media}
              userId={userId}
              onChange={(data) => handleFormChange('media', data)}
            />
          )}
          
          {activeSection === 'availability' && (
            <AvailabilityForm
              data={formData.availability}
              onChange={(data) => handleFormChange('availability', data)}
            />
          )}
          
          {activeSection === 'privacy' && (
            <PrivacySettings
              settings={formData.privacySettings}
              onChange={(data) => handleFormChange('privacySettings', data)}
            />
          )}
        </div>
      </div>

      {/* Exit Confirmation Modal */}
      <ConfirmationModal
        isOpen={showExitConfirmation}
        onClose={() => setShowExitConfirmation(false)}
        onConfirm={handleExitConfirm}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave without saving?"
      />
    </div>
  );
};

export default ProfileEdit;
